import { DEFAULT_PAGE_SIZE } from '../components/constants';
import type {
  ArtistTracksSortBy,
  AssetFamily,
  AssetFamilyData,
  AssetFamilyDataResponse,
  AssetPerformanceResponse,
  ContributorTypeFilter,
  DspType,
  EngagementMetrics,
  PerformanceMetrics,
  PeriodComparison,
  StreamingMetrics,
} from './types';
import { calculateDeltaRatio } from './utils';

const formatArtistTracksData = (
  data: AssetFamilyDataResponse,
): {
  items: AssetFamily[];
  pagination: {
    offset: number;
    total: number;
  };
} => {
  return {
    items: data.items.map((item) => ({
      id: item.family_id,
      title: item.grouping_title,
      description: item.description,
      initialReleaseDate: item.earliest_release_date,
      image: item.image,
      links: {
        opus: `https://opus.wmg.com/entity/track/GRP-${item.family_id}00000000`,
      },
      contributors: item.contributors?.map((contributor) => ({
        partyId: contributor.party_id,
        uaid: contributor.uaid,
        name: contributor.display_name,
        categories: contributor.contributor_categories,
        image: contributor.image,
      })),
    })),
    pagination: {
      offset: data.pagination_offset.offset,
      total: data.pagination_offset.total,
    },
  };
};

const formatAssetFamilyData = (data: AssetFamilyData): AssetFamily => {
  return {
    id: data.family_id,
    title: data.grouping_title,
    description: data.description,
    initialReleaseDate: data.earliest_release_date,
    image: data.image,
  };
};

export const getArtistTracks = async ({
  uaid,
  limit = DEFAULT_PAGE_SIZE,
  offset = 0,
  sortBy,
  searchText,
  startDate,
  endDate,
  isReleased,
  contributorType,
}: {
  uaid?: number | null;
  limit?: number;
  offset?: number;
  sortBy?: ArtistTracksSortBy;
  searchText?: string;
  // Day to start filtering tracks by, this is inclusive. If not provided, we will get all tracks until end_date.
  startDate?: string;
  // Day to end filtering tracks by, this is inclusive. If not provided, we will get all tracks from start_date until now.
  endDate?: string;
  isReleased?: boolean;
  contributorType?: ContributorTypeFilter;
}) => {
  if (!uaid) {
    throw new Error('UAID is required');
  }

  try {
    const params = new URLSearchParams();

    if (uaid) {
      params.set('uaid', String(uaid));
    }

    params.set('limit', String(limit));

    if (offset) {
      params.set('offset', String(offset));
    }

    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    if (searchText) {
      params.set('partial_title', searchText);
    }

    if (startDate) {
      params.set('start_date', startDate);
    }

    if (endDate) {
      params.set('end_date', endDate);
    }

    if (isReleased !== undefined) {
      params.set('is_released', `${isReleased}`);
    }

    if (contributorType && contributorType !== 'ALL') {
      params.set('contributor_type_filter', `[${contributorType}]`);
    }

    const response = await fetch(
      `/api/music_api/v1/asset-families?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch artist tracks: ${response.statusText}`);
    }

    const data: AssetFamilyDataResponse = await response.json();

    return formatArtistTracksData(data);
  } catch (error) {
    console.error('Error fetching artists:', error);

    throw error;
  }
};

export const getAssetByFamilyId = async ({
  familyId,
}: {
  familyId: string;
}) => {
  if (!familyId) {
    throw new Error('familyId is required');
  }

  try {
    const response = await fetch(
      `/api/music_api/v1/asset-families/${familyId}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch asset by familyId: ${response.statusText}`,
      );
    }

    const data: AssetFamilyData = await response.json();

    return formatAssetFamilyData(data);
  } catch (error) {
    console.error('Error fetching asset by familyId:', error);

    throw error;
  }
};

const formatPeriodComparison = (
  current: number,
  previous: number,
): PeriodComparison => ({
  current,
  previous,
  deltaRatio: calculateDeltaRatio({ current, previous }),
});

const formatStreamingMetric = (
  item: AssetPerformanceResponse['tracks'][number]['streaming_by_dsp'][number],
): StreamingMetrics => ({
  totalStreams: item.total_streams,
  periodStreams: formatPeriodComparison(
    item.this_period_streams,
    item.previous_period_streams,
  ),
  organicStreams: formatPeriodComparison(
    item.this_period_organic_streams,
    item.previous_period_organic_streams,
  ),
  // @TODO WMG1-273 Waiting for programmed streams data
});

const formatEngagementMetric = (
  item: AssetPerformanceResponse['tracks'][number]['engagement_by_dsp'][number],
): EngagementMetrics => ({
  socialCreations: formatPeriodComparison(
    item.this_period_social_creations,
    item.previous_period_social_creations,
  ),
});

const formatTrackPerformanceData = (data: AssetPerformanceResponse) => {
  const formatted = data.tracks.map((track) => {
    const streamingDspMap: Record<DspType, StreamingMetrics> = {};
    const engagementDspMap: Record<DspType, EngagementMetrics> = {};

    let aggregatedStreams: StreamingMetrics | undefined;
    let aggregatedEngagement: EngagementMetrics | undefined;

    track.streaming_by_dsp.forEach((item) => {
      const metric = formatStreamingMetric(item);

      if (item.dsp === 'all') {
        aggregatedStreams = metric;
      } else {
        streamingDspMap[item.dsp] = metric;
      }
    });
    track.engagement_by_dsp.forEach((item) => {
      const metric = formatEngagementMetric(item);

      engagementDspMap[item.dsp] = metric;

      if (item.dsp === 'all') {
        aggregatedEngagement = metric;
      }
    });

    return {
      familyId: Number(track.family_id),
      performance: {
        streaming: {
          aggregated: aggregatedStreams!,
          dspMap: streamingDspMap,
        },
        engagement: {
          aggregated: aggregatedEngagement!,
          dspMap: engagementDspMap,
        },
        streamingBenchmark: track.streaming_benchmark.baseline_benchmark,
      },
    };
  });

  return new Map<number, PerformanceMetrics>(
    formatted.map((item) => [item.familyId, item.performance]),
  );
};

export const getArtistTracksPerformance = async ({
  familyIds = [],
  startDate,
  endDate,
}: {
  familyIds?: number[];
  startDate?: string;
  endDate?: string;
}) => {
  try {
    const response = await fetch(
      `/api/mixone/api/v1/tracks/performance/${familyIds.join(',')}?start_date=${startDate}&end_date=${endDate}&period=weekly&sort_by=release_date&page=0&page_size=${DEFAULT_PAGE_SIZE}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch artist tracks performance: ${response.statusText}`,
      );
    }

    const data: AssetPerformanceResponse = await response.json();

    return formatTrackPerformanceData(data);
  } catch (error) {
    console.error('Error fetching artists:', error);

    throw error;
  }
};

export function getMockPerformanceData(familyId: number) {
  return {
    familyId,
    performance_over_time: {
      release_age_day: 12,
      metrics: [
        {
          date: '2024-09-01',
          streams: 65000,
          organic_streams: {
            library: { value: 30000, delta: -3 },
            search: { value: 15000, delta: 2 },
          },
          programmed_streams: 35000,
        },
        {
          date: '2024-09-02',
          streams: 120000,
          organic_streams: {
            library: { value: 50000, delta: 8 },
            search: { value: 25000, delta: 5 },
          },
          programmed_streams: 45000,
        },
        {
          date: '2024-09-03',
          streams: 90000,
          organic_streams: {
            library: { value: 35000, delta: -6 },
            search: { value: 18000, delta: -1 },
          },
          programmed_streams: 37000,
        },
        {
          date: '2024-09-04',
          streams: 160000,
          organic_streams: {
            library: { value: 75000, delta: 12 },
            search: { value: 40000, delta: 8 },
          },
          programmed_streams: 45000,
        },
        {
          date: '2024-09-05',
          streams: 110000,
          organic_streams: {
            library: { value: 45000, delta: -4 },
            search: { value: 22000, delta: -2 },
          },
          programmed_streams: 43000,
        },
        {
          date: '2024-09-06',
          streams: 130000,
          organic_streams: {
            library: { value: 60000, delta: 7 },
            search: { value: 28000, delta: 4 },
          },
          programmed_streams: 42000,
        },
      ],
    },
  };
}

export const getTrackPerformanceOverTime = async ({
  familyId,
}: {
  familyId: number;
}) => {
  // @TODO WMG1-287 Waiting for performance over time data
  return Promise.resolve(getMockPerformanceData(familyId));
};
